import { RotateCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Surface } from '@/components/ui/surface'
import { cn } from '@/lib/utils'

export interface ErrorCardProps {
  /** What failed; defaults to "Laden mislukt". A specific title ("De Journey kon niet worden geladen") reads best. */
  title?: string
  /** What to do. The default says to retry and, failing that, to report it. */
  description?: string
  /** Called when the user presses "Opnieuw proberen". */
  onRetry: () => void
  /** `bare` drops the frame, for use inside a card (no frame in a frame). */
  variant?: 'card' | 'bare'
  className?: string
}

export const ERROR_CARD_TITLE = 'Laden mislukt'
export const ERROR_CARD_DESCRIPTION =
  'Er ging iets mis. Probeer het opnieuw; lukt het niet, meld het dan bij de beheerder.'

/**
 * The error state of a failed load (spec-64-u2, U-072 / DS-13): an icon, what failed, what
 * to do, and "Opnieuw proberen". Laid out like `EmptyState`: a one-column grid at full
 * width with `justify-items-center`, so the text no longer collapses into a column of one
 * word per line (the shadcn `CardHeader` was an `@container` without a width of its own).
 */
export function ErrorCard({
  title = ERROR_CARD_TITLE,
  description = ERROR_CARD_DESCRIPTION,
  onRetry,
  variant = 'card',
  className,
}: ErrorCardProps) {
  const body = (
    <>
      <span className="flex size-10 items-center justify-center rounded-full bg-danger-subtle text-danger">
        <TriangleAlert className="size-5" aria-hidden />
      </span>
      <p className="font-heading text-base font-semibold text-foreground">{title}</p>
      {description ? <p className="max-w-prose text-sm text-muted-foreground">{description}</p> : null}
      <Button type="button" variant="outline" className="mt-2" onClick={onRetry}>
        <RotateCw aria-hidden />
        Opnieuw proberen
      </Button>
    </>
  )
  const layout = cn('grid grid-cols-1 justify-items-center gap-2 py-8 text-center', className)
  if (variant === 'bare') {
    return (
      <div role="alert" data-slot="error-card" className={layout}>
        {body}
      </div>
    )
  }
  return (
    <Surface variant="inset" padding="md" role="alert" data-slot="error-card" className={layout}>
      {body}
    </Surface>
  )
}
